import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { updateAgent } from '../data';
import { Bot, Target } from 'lucide-react';
import './AgentPages.css';

const industries = [
  'Customer Support', 'Sales', 'IT Helpdesk', 'Healthcare',
  'E-commerce', 'Finance', 'Education', 'Other'
];

const roleSuggestions = [
  "Customer support specialist",
  "Sales assistant",
  "Technical advisor",
  "Appointment scheduler"
];

const AUTOSAVE_DELAY_MS = 800;

export default function AgentIdentity() {
  const { agent, setAgent, agentId } = useOutletContext();
  const [status, setStatus] = useState('idle'); // 'idle' | 'pending' | 'saving' | 'saved' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const [form, setForm] = useState({
    name: '', role: '', industry: '', objective: '', successDefinition: ''
  });

  const hydratedRef = useRef(false);
  const timerRef = useRef(null);
  const latestRef = useRef(form);
  const savedTimerRef = useRef(null);

  useEffect(() => {
    if (agent) {
      const next = {
        name: agent.name || '',
        role: agent.role || '',
        industry: agent.industry || '',
        objective: agent.objective || '',
        successDefinition: agent.successDefinition || ''
      };
      setForm(next);
      latestRef.current = next;
      hydratedRef.current = true;
    }
  }, [agent]);

  const flushSave = useCallback(async () => {
    const payload = latestRef.current;
    setStatus('saving');
    setErrorMsg('');
    try {
      await updateAgent(agentId, payload);
      setAgent((prev) => ({ ...(prev || {}), ...payload }));
      setStatus('saved');
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => {
        setStatus((s) => (s === 'saved' ? 'idle' : s));
      }, 1500);
    } catch (err) {
      console.error('Auto-save failed:', err);
      setErrorMsg(err?.message || 'Save failed');
      setStatus('error');
    }
  }, [agentId, setAgent]);

  const scheduleSave = useCallback(() => {
    if (!hydratedRef.current) return;
    setStatus('pending');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      flushSave();
    }, AUTOSAVE_DELAY_MS);
  }, [flushSave]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  useEffect(() => {
    const onBeforeUnload = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        flushSave();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [flushSave]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      latestRef.current = next;
      return next;
    });
    scheduleSave();
  };

  const addRoleSuggestion = (suggestion) => {
    handleChange('role', suggestion);
  };

  const statusLabel = (() => {
    switch (status) {
      case 'pending': return 'Editing…';
      case 'saving': return 'Saving…';
      case 'saved': return 'Saved';
      case 'error': return errorMsg || 'Save failed';
      default: return 'All changes saved';
    }
  })();

  return (
    <div className="config-page">
      <div className="config-header">
        <h1>Identity & Purpose</h1>
        <p>Define who your agent is and what it's designed to accomplish</p>
        <span className={`autosave-badge autosave-${status}`}>{statusLabel}</span>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><Bot size={18} /></div>
          <div className="section-text">
            <h2>Basic Information</h2>
            <p>Give your agent a name and define its role</p>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="label">Agent Name</label>
            <input
              type="text"
              className="input"
              placeholder="Customer Success Bot"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="label">Industry</label>
            <select
              className="input select"
              value={form.industry}
              onChange={(e) => handleChange('industry', e.target.value)}
            >
              <option value="">Select industry</option>
              {industries.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="label">Role</label>
          <input
            type="text"
            className="input"
            placeholder="e.g., Customer support specialist"
            value={form.role}
            onChange={(e) => handleChange('role', e.target.value)}
          />
          <div className="suggestions">
            <div className="suggestions-title">Quick suggestions</div>
            <div className="suggestions-list">
              {roleSuggestions.map((s, i) => (
                <button key={i} className="suggestion" onClick={() => addRoleSuggestion(s)}>
                  + {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="config-section">
        <div className="section-header">
          <div className="section-icon"><Target size={18} /></div>
          <div className="section-text">
            <h2>Mission</h2>
            <p>What your agent is designed to accomplish</p>
          </div>
        </div>

        <div className="form-group">
          <label className="label">Primary Objective</label>
          <textarea
            className="input textarea"
            placeholder="Resolve customer issues quickly with accurate, helpful responses..."
            value={form.objective}
            onChange={(e) => handleChange('objective', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="label">Success Looks Like</label>
          <textarea
            className="input textarea"
            placeholder="Customer's issue is resolved in their first interaction..."
            value={form.successDefinition}
            onChange={(e) => handleChange('successDefinition', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}
